import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop — reset scroll position when the pathname changes.
 *
 * React Router v6 does not auto-scroll on route change; without this,
 * clicking a link at the bottom of one page (e.g., the "Who it's for"
 * cards) opens the next page scrolled to the same Y-offset, which looks
 * like the app landed in the middle of the new page.
 *
 * Behavior:
 *   - New pathname WITHOUT a hash → scroll to top (0, 0).
 *   - New pathname WITH a hash    → try to scroll that element into view
 *     (browsers do not fire hash navigation on SPA route transitions).
 *   - Hash-only change on same pathname → do nothing (native anchor
 *     scrolling handles it, and we don't want to override it).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Defer one frame so the destination DOM is mounted before we look
      // up the target element.
      requestAnimationFrame(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          window.scrollTo(0, 0);
        }
      });
    } else {
      window.scrollTo(0, 0);
    }
    // Re-run on both pathname AND hash changes — matches native browser
    // behavior (scroll to top on new page, scroll to element on hash target).
  }, [pathname, hash]);

  return null;
}
